import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Divider,
  IconButton,
  InputAdornment,
  Link,
  MenuItem,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Eye,
  EyeOff,
  GraduationCap,
  Layers,
  ShieldCheck,
} from 'lucide-react';
import {
  gradeOptions,
  getClassLevelOptionsByCountry,
  getClassLevelSystemLabelByCountry,
  getExamOptionsByCountry,
  getExamSystemLabelByCountry,
} from '../../config';
import { sendEmailVerificationOtp, verifyEmailOtp } from '../../services/authService';
import { countryOptions } from '../../utils/countryOptions';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const isValidEmail = (value: string) => EMAIL_REGEX.test(value);

interface FormData {
  name: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
  country: string;
  board: string;
  currentGrade: string;
  gradeExpectation: string;
  gradeMode: 'grade' | 'percentage' | 'performance';
  classLevel: string;
  weakness: string;
  strength: string;
}

interface FormErrors {
  name?: string;
  lastName?: string;
  email?: string;
  password?: string;
  confirmPassword?: string;
  country?: string;
  currentGrade?: string;
  gradeExpectation?: string;
  classLevel?: string;
}

interface AuthFormProps {
  isLogin?: boolean;
  onSubmit: (data: FormData) => Promise<void | { completed?: boolean }>;
}

const registerSteps = [
  {
    title: 'Account',
    description: 'Tell us who you are',
  },
  {
    title: 'Academic profile',
    description: 'Where and what you study',
  },
  {
    title: 'Performance goals',
    description: 'Targets for the AI tutor',
  },
];

const AuthForm: React.FC<AuthFormProps> = ({ isLogin = false, onSubmit }) => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    country: '',
    board: '',
    currentGrade: '',
    gradeExpectation: '',
    gradeMode: 'grade',
    classLevel: '',
    weakness: '',
    strength: '',
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [selectedLevelType, setSelectedLevelType] = useState<'className' | 'examName'>('className');
  const [isCustomClassLevel, setIsCustomClassLevel] = useState(false);
  const [customClassLevel, setCustomClassLevel] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [emailOtp, setEmailOtp] = useState('');
  const [isOtpSending, setIsOtpSending] = useState(false);
  const [isOtpVerifying, setIsOtpVerifying] = useState(false);
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [otpCountdown, setOtpCountdown] = useState(0);
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [otpError, setOtpError] = useState<string | null>(null);
  const isCountrySelected = Boolean(formData.country.trim());
  const isEducationSelectionDisabled = !isCountrySelected;

  useEffect(() => {
    if (!isOtpSent || otpCountdown === 0) return;
    const timer = setInterval(() => {
      setOtpCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [isOtpSent, otpCountdown]);

  const validateStep = (stepNumber: number): boolean => {
    const newErrors: FormErrors = {};

    if (stepNumber === 1) {
      if (!formData.name.trim()) newErrors.name = 'First name is required';
      if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
      if (!formData.email.trim()) {
        newErrors.email = 'Email is required';
      } else if (!isValidEmail(formData.email)) {
        newErrors.email = 'Please enter a valid email';
      } else if (!isEmailVerified) {
        newErrors.email = 'Please verify your email with the OTP sent to you';
      }
      if (!formData.password) {
        newErrors.password = 'Password is required';
      } else if (formData.password.length < 6) {
        newErrors.password = 'Password must be at least 6 characters';
      }
      if (!formData.confirmPassword) {
        newErrors.confirmPassword = 'Please confirm your password';
      } else if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    }

    if (stepNumber === 2) {
      if (!formData.country) newErrors.country = 'Country is required';
      if (!formData.classLevel.trim()) {
        newErrors.classLevel =
          selectedLevelType === 'className'
            ? 'Class level is required'
            : 'Exam name is required';
      }
    }

    if (stepNumber === 3) {
      if (!formData.currentGrade) newErrors.currentGrade = 'Current grade is required';
      if (!formData.gradeExpectation)
        newErrors.gradeExpectation = 'Expected grade is required';

      if (formData.gradeMode === 'percentage') {
        const percentage = parseInt(formData.currentGrade, 10);
        if (Number.isNaN(percentage) || percentage < 1 || percentage > 100) {
          newErrors.currentGrade = 'Please enter a percentage between 1 and 100';
        }

        const expectedPercentage = parseInt(formData.gradeExpectation, 10);
        if (
          Number.isNaN(expectedPercentage) ||
          expectedPercentage < 1 ||
          expectedPercentage > 100
        ) {
          newErrors.gradeExpectation = 'Please enter a percentage between 1 and 100';
        }
      } else if (formData.gradeMode === 'performance') {
        const performance = parseInt(formData.currentGrade, 10);
        if (Number.isNaN(performance) || performance < 1 || performance > 5) {
          newErrors.currentGrade = 'Please enter a performance between 1 and 5';
        }

        const expectedPerformance = parseInt(formData.gradeExpectation, 10);
        if (
          Number.isNaN(expectedPerformance) ||
          expectedPerformance < 1 ||
          expectedPerformance > 5
        ) {
          newErrors.gradeExpectation = 'Please enter a performance between 1 and 5';
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleGradeChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = event.target;

    if (formData.gradeMode === 'percentage') {
      const numValue = parseInt(value, 10);
      if (
        value === '' ||
        (!Number.isNaN(numValue) && numValue >= 1 && numValue <= 100)
      ) {
        setFormData((prev) => ({ ...prev, [name]: value }));
      }
    } else if (formData.gradeMode === 'performance') {
      const numValue = parseInt(value, 10);
      if (
        value === '' ||
        (!Number.isNaN(numValue) && numValue >= 1 && numValue <= 5)
      ) {
        setFormData((prev) => ({ ...prev, [name]: value }));
      }
    } else if (name === 'country') {
      const currentOptions =
        selectedLevelType === 'className'
          ? getClassLevelOptionsByCountry(formData.country)
          : getExamOptionsByCountry(formData.country);
      const nextOptions =
        selectedLevelType === 'className'
          ? getClassLevelOptionsByCountry(value)
          : getExamOptionsByCountry(value);
      const shouldResetSelection =
        Boolean(formData.classLevel) &&
        currentOptions.includes(formData.classLevel) &&
        !nextOptions.includes(formData.classLevel);

      setFormData((prev) => {
        return {
          ...prev,
          country: value,
          classLevel: shouldResetSelection ? '' : prev.classLevel,
        };
      });

      if (shouldResetSelection) {
        setIsCustomClassLevel(false);
        setCustomClassLevel('');
      }
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }

    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = event.target;

    if (name === 'classLevel') {
      if (value === '__custom__') {
        setIsCustomClassLevel(true);
        setCustomClassLevel('');
        setFormData((prev) => ({ ...prev, classLevel: '' }));
      } else {
        setIsCustomClassLevel(false);
        setCustomClassLevel('');
        setFormData((prev) => ({ ...prev, classLevel: value }));
      }
    } else if (name === 'currentGrade' || name === 'gradeExpectation') {
      return;
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }

    if (name === 'email') {
      setIsOtpSent(false);
      setEmailOtp('');
      setIsEmailVerified(false);
      setOtpCountdown(0);
      setOtpError(null);
    }

    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleCustomClassChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setCustomClassLevel(value);
    setFormData((prev) => ({ ...prev, classLevel: value }));

    if (errors.classLevel) {
      setErrors((prev) => ({ ...prev, classLevel: undefined }));
    }
  };

  const handleLevelTypeChange = (type: 'className' | 'examName') => {
    setSelectedLevelType(type);
    setIsCustomClassLevel(false);
    setCustomClassLevel('');
    setFormData((prev) => ({ ...prev, classLevel: '' }));
    setErrors((prev) => ({ ...prev, classLevel: undefined }));
  };

  const handleGradeModeChange = (
    _: React.MouseEvent<HTMLElement>,
    value: 'grade' | 'percentage' | 'performance' | null,
  ) => {
    if (!value) return;

    setFormData((prev) => ({
      ...prev,
      gradeMode: value,
      currentGrade: '',
      gradeExpectation: '',
    }));
    setErrors((prev) => ({
      ...prev,
      currentGrade: undefined,
      gradeExpectation: undefined,
    }));
  };

  const handleSendOtp = async () => {
    if (!formData.email.trim()) {
      setErrors((prev) => ({ ...prev, email: 'Email is required' }));
      return;
    }

    if (!isValidEmail(formData.email)) {
      setErrors((prev) => ({ ...prev, email: 'Please enter a valid email' }));
      return;
    }

    setIsOtpSending(true);
    setOtpError(null);

    try {
      await sendEmailVerificationOtp(formData.email);
      toast.success('OTP sent to your email');
      setIsOtpSent(true);
      setEmailOtp('');
      setIsEmailVerified(false);
      setOtpCountdown(60);
      setErrors((prev) => ({ ...prev, email: undefined }));
    } catch (error: any) {
      const message =
        error?.response?.data?.msg || error?.message || 'Failed to send OTP';
      toast.error(message);
      setOtpError(message);
    } finally {
      setIsOtpSending(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (emailOtp.length !== 6) {
      setOtpError('Enter the 6-digit OTP sent to your email');
      return;
    }

    setIsOtpVerifying(true);
    setOtpError(null);

    try {
      await verifyEmailOtp(formData.email, emailOtp);
      toast.success('Email verified successfully');
      setIsEmailVerified(true);
      setOtpCountdown(0);
      setErrors((prev) => ({ ...prev, email: undefined }));
    } catch (error: any) {
      const message =
        error?.response?.data?.msg || error?.message || 'OTP verification failed';
      toast.error(message);
      setOtpError(message);
      setIsEmailVerified(false);
    } finally {
      setIsOtpVerifying(false);
    }
  };

  const handleOtpInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value.replace(/\D/g, '').slice(0, 6);
    setEmailOtp(value);
    if (otpError) setOtpError(null);
  };

  const nextStep = () => {
    if (validateStep(step)) {
      setStep((prev) => prev + 1);
    }
  };

  const prevStep = () => {
    setStep((prev) => prev - 1);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoading(true);
    try {
      if (!isLogin && formData.password !== formData.confirmPassword) {
        toast.error('Passwords do not match');
        return;
      }

      // For login, only send email and password
      const submitData = isLogin
        ? { email: formData.email, password: formData.password }
        : formData;

      const result = await onSubmit(submitData as FormData);
      if (result?.completed !== false) {
        toast.success(isLogin ? 'Login successful' : 'Registration successful');
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const classLevelSelectOptions = useMemo(() => {
    const base =
      selectedLevelType === 'className'
        ? getClassLevelOptionsByCountry(formData.country)
        : getExamOptionsByCountry(formData.country);
    return [...base, 'Other (custom)'];
  }, [formData.country, selectedLevelType]);
  const classLevelHelperText =
    errors.classLevel ||
    (isEducationSelectionDisabled
        ? 'Select a country first to load the correct class list.'
        : selectedLevelType === 'className'
          ? `${getClassLevelSystemLabelByCountry(formData.country)} loaded for ${formData.country}.`
          : `${getExamSystemLabelByCountry(formData.country)} loaded for ${formData.country}.`);

  const heroHighlights = [
    'AI-personalised revision sprints',
    'Adaptive mocks with instant marking',
    'Parent-ready progress analytics',
  ];

  const renderStepContent = () => {
    if (isLogin) {
      return (
        <Stack spacing={3}>
          <TextField
            label="Email"
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            fullWidth
            error={Boolean(errors.email)}
            helperText={errors.email}
            autoComplete="email"
          />

          <TextField
            label="Password"
            type={showPassword ? 'text' : 'password'}
            name="password"
            value={formData.password}
            onChange={handleChange}
            required
            fullWidth
            error={Boolean(errors.password)}
            helperText={errors.password}
            autoComplete="current-password"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Link component={RouterLink} to="/forgot-password" underline="hover">
              Forgot password?
            </Link>
          </Stack>

          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={loading}
          >
            {loading ? 'Logging in…' : 'Login'}
          </Button>
        </Stack>
      );
    }

    switch (step) {
      case 1:
        return (
          <Grid container spacing={2.5}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="First name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                fullWidth
                error={Boolean(errors.name)}
                helperText={errors.name}
                autoComplete="given-name"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Last name"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                required
                fullWidth
                error={Boolean(errors.lastName)}
                helperText={errors.lastName}
                autoComplete="family-name"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Email"
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                fullWidth
                error={
                  Boolean(errors.email) ||
                  (formData.email.trim().length > 0 && !isValidEmail(formData.email))
                }
                helperText={
                  errors.email ||
                  (formData.email.trim().length > 0 && !isValidEmail(formData.email)
                    ? 'Please enter a valid email'
                    : undefined)
                }
                autoComplete="email"
              />
            </Grid>
            {!isLogin && (
              <Grid item xs={12}>
                <Stack spacing={1.5}>
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1.5}
                    alignItems={{ xs: 'stretch', sm: 'center' }}
                  >
                    <Button
                      variant="outlined"
                      onClick={handleSendOtp}
                      disabled={
                        isOtpSending ||
                        !formData.email.trim() ||
                        !isValidEmail(formData.email) ||
                        isEmailVerified ||
                        otpCountdown > 0
                      }
                      sx={{ minWidth: { sm: 180 } }}
                    >
                      {isOtpSending
                        ? 'Sending code...'
                        : isEmailVerified
                          ? 'Email verified'
                          : otpCountdown > 0
                            ? `Resend in ${otpCountdown}s`
                            : isOtpSent
                              ? 'Resend OTP'
                              : 'Send OTP'}
                    </Button>
                    {!isEmailVerified && isOtpSent && otpCountdown > 0 && (
                      <Typography variant="caption" color="text.secondary">
                        You can request a new code in {otpCountdown}s
                      </Typography>
                    )}
                  </Stack>

                  {isOtpSent && !isEmailVerified && (
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      spacing={1.5}
                      alignItems={{ xs: 'stretch', sm: 'flex-start' }}
                    >
                      <TextField
                        label="Enter 6-digit OTP"
                        value={emailOtp}
                        onChange={handleOtpInputChange}
                        fullWidth
                        inputProps={{
                          inputMode: 'numeric',
                          pattern: '[0-9]*',
                          maxLength: 6,
                        }}
                        error={Boolean(otpError)}
                        helperText={otpError || 'Code sent to your inbox'}
                      />
                      <Button
                        variant="contained"
                        onClick={handleVerifyOtp}
                        disabled={isOtpVerifying}
                        sx={{ minWidth: { sm: 160 } }}
                      >
                        {isOtpVerifying ? 'Verifying...' : 'Verify OTP'}
                      </Button>
                    </Stack>
                  )}

                  {isEmailVerified && (
                    <Alert severity="success" sx={{ mt: 1 }}>
                      Email verified. You can continue registration.
                    </Alert>
                  )}
                </Stack>
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Password"
                type={showPassword ? 'text' : 'password'}
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
                fullWidth
                error={Boolean(errors.password)}
                helperText={errors.password}
                autoComplete="new-password"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Confirm password"
                type={showConfirmPassword ? 'text' : 'password'}
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
                fullWidth
                error={Boolean(errors.confirmPassword)}
                helperText={errors.confirmPassword}
                autoComplete="new-password"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        edge="end"
                      >
                        {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
        );
      case 2:
        return (
          <Stack spacing={3}>
            <TextField
              select
              label="Country"
              name="country"
              value={formData.country}
              onChange={handleChange}
              required
              fullWidth
              error={Boolean(errors.country)}
              helperText={errors.country}
            >
              {countryOptions.map((country) => (
                <MenuItem key={country} value={country}>
                  {country}
                </MenuItem>
              ))}
            </TextField>

            <Stack spacing={1}>
              <Typography variant="subtitle2" color="text.secondary">
                Are you selecting a class level or exam name?
              </Typography>
              <ToggleButtonGroup
                value={selectedLevelType}
                exclusive
                onChange={(_, value) => value && handleLevelTypeChange(value)}
                fullWidth
                color="primary"
              >
                <ToggleButton value="className">Class level</ToggleButton>
                <ToggleButton value="examName">Exam name</ToggleButton>
              </ToggleButtonGroup>
            </Stack>

            <TextField
              select
              label={selectedLevelType === 'className' ? 'Class level' : 'Exam name'}
              name="classLevel"
              value={isCustomClassLevel ? '__custom__' : formData.classLevel}
              onChange={handleChange}
              required
              fullWidth
              disabled={isEducationSelectionDisabled}
              error={Boolean(errors.classLevel)}
              helperText={classLevelHelperText}
            >
              <MenuItem value="">
                <em>Select an option</em>
              </MenuItem>
              {classLevelSelectOptions.map((option) => (
                <MenuItem
                  key={option}
                  value={option === 'Other (custom)' ? '__custom__' : option}
                >
                  {option}
                </MenuItem>
              ))}
            </TextField>

            {isCustomClassLevel && (
              <TextField
                label={
                  selectedLevelType === 'className'
                    ? 'Custom class level'
                    : 'Custom exam name'
                }
                value={customClassLevel}
                onChange={handleCustomClassChange}
                fullWidth
              />
            )}

            <TextField
              label="Board (optional)"
              name="board"
              value={formData.board}
              onChange={handleChange}
              fullWidth
            />
          </Stack>
        );
      case 3:
        return (
          <Stack spacing={3}>
            <Stack spacing={1}>
              <Typography variant="subtitle2" color="text.secondary">
                How do you track performance?
              </Typography>
              <ToggleButtonGroup
                value={formData.gradeMode}
                exclusive
                onChange={handleGradeModeChange}
                fullWidth
              >
                <ToggleButton value="grade">Grade</ToggleButton>
                <ToggleButton value="percentage">Percentage</ToggleButton>
                <ToggleButton value="performance">Performance (1-5)</ToggleButton>
              </ToggleButtonGroup>
            </Stack>

            {formData.gradeMode === 'grade' ? (
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    label="Current grade"
                    name="currentGrade"
                    value={formData.currentGrade}
                    onChange={handleGradeChange}
                    required
                    fullWidth
                    error={Boolean(errors.currentGrade)}
                    helperText={errors.currentGrade}
                  >
                    {gradeOptions.map((grade) => (
                      <MenuItem key={grade} value={grade}>
                        {grade}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    label="Expected grade"
                    name="gradeExpectation"
                    value={formData.gradeExpectation}
                    onChange={handleGradeChange}
                    required
                    fullWidth
                    error={Boolean(errors.gradeExpectation)}
                    helperText={errors.gradeExpectation}
                  >
                    {gradeOptions.map((grade) => (
                      <MenuItem key={grade} value={grade}>
                        {grade}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </Grid>
            ) : (
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    type="number"
                    label={
                      formData.gradeMode === 'percentage'
                        ? 'Current percentage (1-100)'
                        : 'Current performance (1-5)'
                    }
                    name="currentGrade"
                    value={formData.currentGrade}
                    onChange={handleGradeChange}
                    required
                    fullWidth
                    error={Boolean(errors.currentGrade)}
                    helperText={errors.currentGrade}
                    inputProps={{
                      min: formData.gradeMode === 'percentage' ? 1 : 1,
                      max: formData.gradeMode === 'percentage' ? 100 : 5,
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    type="number"
                    label={
                      formData.gradeMode === 'percentage'
                        ? 'Expected percentage (1-100)'
                        : 'Expected performance (1-5)'
                    }
                    name="gradeExpectation"
                    value={formData.gradeExpectation}
                    onChange={handleGradeChange}
                    required
                    fullWidth
                    error={Boolean(errors.gradeExpectation)}
                    helperText={errors.gradeExpectation}
                    inputProps={{
                      min: formData.gradeMode === 'percentage' ? 1 : 1,
                      max: formData.gradeMode === 'percentage' ? 100 : 5,
                    }}
                  />
                </Grid>
              </Grid>
            )}

            <TextField
              label="Key weakness (optional)"
              name="weakness"
              value={formData.weakness}
              onChange={handleChange}
              fullWidth
              multiline
              minRows={2}
            />
            <TextField
              label="Key strength (optional)"
              name="strength"
              value={formData.strength}
              onChange={handleChange}
              fullWidth
              multiline
              minRows={2}
            />
          </Stack>
        );
      default:
        return null;
    }
  };

  return (
    <Card
      sx={{
        width: '100%',
        maxWidth: 980,
        borderRadius: 4,
        overflow: 'hidden',
        boxShadow: '0 40px 70px rgba(15, 23, 42, 0.18)',
      }}
    >
      <Grid container>
        <Grid
          item
          xs={12}
          md={5}
          sx={{
            background:
              'linear-gradient(180deg, rgba(90, 75, 252, 0.92) 0%, rgba(14, 116, 144, 0.92) 100%)',
            color: 'common.white',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
          }}
        >
          <CardContent sx={{ p: { xs: 4, md: 5 } }}>
            <Stack spacing={3}>
              <Box>
                <Typography variant="overline" sx={{ letterSpacing: 2, color: 'rgba(255,255,255,0.72)' }}>
                  ZapTick AI
                </Typography>
                <Typography variant="h4" fontWeight={700}>
                  Your personalised learning cockpit
                </Typography>
                <Typography variant="body2" sx={{ mt: 1.5, color: 'rgba(255,255,255,0.82)' }}>
                  Built with teachers and examiners to accelerate A-Level, GCSE and IB mastery.
                </Typography>
              </Box>

              <Stack spacing={2}>
                {heroHighlights.map((item) => (
                  <Stack key={item} direction="row" spacing={2} alignItems="flex-start">
                    <CheckCircle2 size={20} />
                    <Typography variant="body2" color="rgba(255,255,255,0.82)">
                      {item}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Stack>
          </CardContent>

          <CardMedia
            component="div"
            sx={{
              display: { xs: 'none', md: 'flex' },
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 5,
              py: 4,
              background: 'rgba(255,255,255,0.08)',
              borderTop: '1px solid rgba(255,255,255,0.16)',
            }}
          >
            <Stack spacing={1}>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                Trusted by
              </Typography>
              <Stack direction="row" spacing={3} alignItems="center">
                <Stack direction="row" spacing={1} alignItems="center">
                  <ShieldCheck size={18} />
                  <Typography variant="body2">Head Teachers</Typography>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                  <GraduationCap size={18} />
                  <Typography variant="body2">Exam Coaches</Typography>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Layers size={18} />
                  <Typography variant="body2">Learning Leads</Typography>
                </Stack>
              </Stack>
            </Stack>
          </CardMedia>
        </Grid>

        <Grid item xs={12} md={7}>
          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{ p: { xs: 4, md: 5 }, height: '100%' }}
          >
            <Stack spacing={4} sx={{ height: '100%' }}>
              <Box>
                <Typography variant="h5" fontWeight={700} gutterBottom>
                  {isLogin ? 'Welcome back' : 'Create your ZapTick AI account'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {isLogin
                    ? 'Sign in to access your AI tutor, mocks and analytics.'
                    : 'We’ll use this information to hyper-personalise your tutor experience.'}
                </Typography>
              </Box>

              {!isLogin && (
                <Stepper activeStep={step - 1} alternativeLabel>
                  {registerSteps.map((registerStep) => (
                    <Step key={registerStep.title}>
                      <StepLabel>
                        <Typography variant="subtitle2">{registerStep.title}</Typography>
                      </StepLabel>
                    </Step>
                  ))}
                </Stepper>
              )}

              <Box sx={{ flexGrow: 1 }}>{renderStepContent()}</Box>

              <Stack spacing={2}>
                {!isLogin ? (
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                  >
                    {step > 1 && (
                      <Button
                        type="button"
                        variant="outlined"
                        startIcon={<ArrowLeft size={18} />}
                        onClick={prevStep}
                        fullWidth
                        sx={{ maxWidth: { sm: 160 } }}
                      >
                        Back
                      </Button>
                    )}
                    {step < registerSteps.length ? (
                      <Button
                        type="button"
                        variant="contained"
                        endIcon={<ArrowRight size={18} />}
                        onClick={nextStep}
                        fullWidth
                      >
                        Continue
                      </Button>
                    ) : (
                      <Button type="submit" variant="contained" disabled={loading} fullWidth>
                        {loading ? 'Creating account…' : 'Complete registration'}
                      </Button>
                    )}
                  </Stack>
                ) : null}

                <Divider />

                <Typography variant="body2" color="text.secondary" align="center">
                  {isLogin ? (
                    <>
                      New to Zaptick AI?{' '}
                      <Link component={RouterLink} to="/register" underline="hover">
                        Create an account
                      </Link>
                    </>
                  ) : (
                    <>
                      Already have an account?{' '}
                      <Link component={RouterLink} to="/login" underline="hover">
                        Sign in
                      </Link>
                    </>
                  )}
                </Typography>
              </Stack>
            </Stack>
          </Box>
        </Grid>
      </Grid>
    </Card>
  );
};

export default AuthForm;
